// NEW standalone autocomplete address field for the redesigned ("v2") Track
// Record UI. It is a copy of @roc/ui's AutocompleteAddressField with a more
// robust Google Places init:
//   - idempotent (guarded by a ref, won't re-attach on re-render),
//   - tolerant of the Maps script not being loaded yet (the shared component
//     crashes in that case),
// Kept here (not in @roc/ui) so the shared component stays untouched. These
// fields mount inside the new Track Record dialogs, which is exactly the case
// the shared version did not handle.

import React, { useEffect, useRef, useCallback } from 'react';
import { FormControl, FormHelperText } from '@material-ui/core';
import { FieldLabel, TestDataAttribute, TextField } from '@roc/ui';

export interface AutocompleteAddressFieldV2Props {
  label?: string;
  name?: string;
  value?: string | null;
  onChange?: (name: string, value: number | string) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorText?: string;
  fullWidth?: boolean;
  margin?: any;
  inputClassname?: string;
  standaloneLabel?: boolean;
  testId: string;
  textField?: boolean;
  tooltip?: string;
  showToolTip?: boolean;
  InputLabelProps?: any;
  InputProps?: any;
  compTool?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

const defaultTooltip =
  'Please enter the address in the below format: \nStreetNumber Street Name, City, State Abbreviation, Zipcode\n\nFor e.g.\n12345 Happy Avenue, New York City, NY 10001';

export const AutocompleteAddressFieldV2: React.FC<AutocompleteAddressFieldV2Props> = ({
  textField,
  label,
  name,
  value,
  onChange,
  disabled,
  required,
  readOnly,
  error,
  errorText,
  fullWidth,
  margin,
  inputClassname,
  standaloneLabel,
  testId,
  tooltip,
  showToolTip,
  InputLabelProps,
  InputProps,
  placeholder,
  compTool = false,
  autoFocus = false,
}) => {
  const textFieldId = testId + 'ac-text-field';
  const autocompleteRef = useRef<any>(null);

  const handleChange = useCallback(
    event => {
      onChange(event, name);
    },
    [onChange, name]
  );

  // Init is idempotent (guarded by autocompleteRef) and tolerant of the Google
  // Maps script not being loaded yet — the shared version crashed in that case.
  useEffect(() => {
    const google = (window as any).google;
    if (!google?.maps?.places) return;

    const inputEl = document.getElementById(textFieldId) as HTMLInputElement;
    if (!inputEl) return;

    if (autocompleteRef.current) return;

    const ac = new google.maps.places.Autocomplete(inputEl, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    });

    ac.setFields(['address_component', 'formatted_address', 'geometry']);

    ac.addListener('place_changed', () => {
      const addressObject = ac.getPlace();
      const address = addressObject?.address_components;
      if (address) {
        handleChange({
          target: {
            value: addressObject.formatted_address,
            fullInfo: addressObject,
          },
        });
      }
      // No focus() here — it re-opens the Places suggestion list over the picked address.
    });

    autocompleteRef.current = ac;
  }, [textFieldId, handleChange]);

  return compTool ? (
    <TestDataAttribute id={testId}>
      <FormControl fullWidth={fullWidth} disabled={disabled} error={error}>
        {label && !InputLabelProps && (
          <FieldLabel showToolTip={showToolTip ?? true} tooltip={tooltip ?? defaultTooltip}>
            {required ? label + ' *' : label}
          </FieldLabel>
        )}
        <TextField
          id={textFieldId}
          name={name}
          value={value}
          label={InputLabelProps ? label : ''}
          onChange={handleChange}
          inputProps={{ maxLength: '200' }}
          disabled={disabled || readOnly}
          className={inputClassname}
          margin={margin}
          required={required}
          variant="outlined"
          testId={testId}
          InputLabelProps={InputLabelProps}
          InputProps={InputProps}
          standaloneLabel={standaloneLabel}
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
        <FormHelperText>{errorText}</FormHelperText>
      </FormControl>
    </TestDataAttribute>
  ) : !textField ? (
    <TestDataAttribute id={testId}>
      <FormControl fullWidth={fullWidth} disabled={disabled} error={error}>
        {label && standaloneLabel && (
          <FieldLabel showToolTip={showToolTip ?? true} tooltip={tooltip ?? defaultTooltip}>
            {required ? label + ' *' : label}
          </FieldLabel>
        )}
        <TextField
          id={textFieldId}
          name={name}
          label={standaloneLabel ? null : label}
          value={value}
          onChange={handleChange}
          inputProps={{ maxLength: '200' }}
          className={inputClassname}
          standaloneLabel={standaloneLabel}
          margin={margin}
          error={error}
          required={required}
          variant="outlined"
          testId={testId}
          disabled={disabled || readOnly}
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
        <FormHelperText>{errorText}</FormHelperText>
      </FormControl>
    </TestDataAttribute>
  ) : (
    <TextField
      id={textFieldId}
      label={label}
      name={name}
      value={value}
      onChange={handleChange}
      disabled={disabled || readOnly}
      required={required}
      error={error}
      helperText={errorText}
      fullWidth={fullWidth}
      margin={margin}
      standaloneLabel={standaloneLabel}
      testId={testId}
      variant="outlined"
      autoFocus={autoFocus}
      placeholder={placeholder}
    />
  );
};

export default AutocompleteAddressFieldV2;
